import { Link } from "react-router-dom";
import { propertyTypeLabels, usePropertiesList } from "../properties/api.js";
import { PropertyIconBadge } from "../properties/PropertyIconBadge.js";
import { ButtonLink } from "../ui/Button.js";
import { EmptyState } from "../ui/EmptyState.js";
import { BuildingIcon, ChevronRightIcon, PlusIcon } from "../ui/icons.js";
import { SkeletonCard } from "../ui/Skeleton.js";

export function AppProperties() {
  const { data: properties, isLoading, error } = usePropertiesList();

  return (
    <div className="mx-auto max-w-md space-y-5 p-4 pt-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Properties</h1>
        <ButtonLink to="/app/properties/new" size="sm" className="rounded-full">
          <PlusIcon className="h-4 w-4" />
          Add
        </ButtonLink>
      </header>

      {isLoading && (
        <ul className="space-y-3" aria-hidden>
          {Array.from({ length: 2 }).map((_, i) => (
            <li key={i}>
              <SkeletonCard />
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          Couldn&apos;t load properties. {error instanceof Error ? error.message : ""}
        </p>
      )}

      {properties && properties.length === 0 && (
        <EmptyState
          icon={<BuildingIcon className="h-7 w-7" />}
          title="Add your first property"
          description="Properties tell pros where the work happens. You'll need at least one to submit a request."
          action={
            <ButtonLink to="/app/properties/new" size="lg" fullWidth>
              Add a property
            </ButtonLink>
          }
        />
      )}

      {properties && properties.length > 0 && (
        <ul className="space-y-3">
          {properties.map((p) => (
            <li key={p.id}>
              <Link
                to={`/app/properties/${p.id}`}
                className="press flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50"
              >
                <PropertyIconBadge property={p} className="h-12 w-12" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-slate-900">{p.nickname}</p>
                  <p className="mt-0.5 truncate text-sm text-slate-600">
                    {propertyTypeLabels[p.propertyType]}
                  </p>
                  <p className="mt-1 truncate text-sm text-slate-500">
                    {p.street}
                    {p.unit ? `, ${p.unit}` : ""} · {p.city}, {p.state} {p.postalCode}
                  </p>
                </div>
                <ChevronRightIcon className="mt-1 h-5 w-5 shrink-0 text-slate-300" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
